import { useEffect, useRef } from 'react';

/**
 * Scrolls the page to the results block (#results / [data-results]) when the
 * user arrives on a listing page with an active search (from the homepage
 * search form, brand tiles, city links, etc.) or when filters change.
 *
 * The position is applied once after the results are ready. Repeated scroll
 * corrections visibly fight the router during pagination.
 */
export function useScrollToResults(options: {
  /** Unique key for the current search (usually pathname + search string). */
  key: string;
  /** Whether there is an active search/filter worth scrolling to. */
  active: boolean;
  /** Whether results finished loading. */
  ready: boolean;
}) {
  const { key, active, ready } = options;
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!active || !ready) return undefined;
    if (lastKeyRef.current === key) return undefined;

    lastKeyRef.current = key;

    const frameId = window.requestAnimationFrame(() => {
      const el = (document.getElementById('results') ||
        document.querySelector('[data-results]')) as HTMLElement | null;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      if (Math.abs(window.scrollY - top) > 8) {
        window.scrollTo({ top: Math.max(top, 0), behavior: 'auto' });
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [key, active, ready]);
}
